/**
 * PATH: packages/email-hub/src/feature/compose/undoSendSync.ts
 * FILE_ID: EMAIL-COMPOSE-LIB-UNDOSENDSYNC
 * PURPOSE: Hydrate the synchronous undo-send cache (@quotech/ui undoSendPref)
 *          from the backend email preferences so the composer's "undo send"
 *          window reflects the user's saved choice on EVERY device — not just
 *          the one where they opened Settings.
 * WHY: The undo-send window is persisted server-side (undo_send_seconds in
 *      /api/settings/email/) so it follows the user. But the hold is created
 *      synchronously at composer mount and can't await a fetch, so the composer
 *      reads a localStorage cache. On a fresh device that cache is the default
 *      until something fills it — this background fetch closes that gap.
 * FLOW:
 *   composer mount → syncUndoSendFromServer() ──(throttled, TTL)──▶
 *     GET /api/settings/email/ → setUndoSendSeconds(undo_send_seconds)
 *   → next composer open reads the corrected cached window.
 * DEPENDENCIES:
 *   - ../lib/api (emailApiFetch): tenant/auth-aware fetch
 *   - @quotech/ui (setUndoSendSeconds): the cache writer
 * RELATED:
 *   - pages/ComposePage.tsx: calls this on mount.
 *   - packages/ui/.../settings/EmailTab.tsx: the picker + same-device writer.
 */
import { setUndoSendSeconds } from '@quotech/ui'

import { emailApiFetch } from '../lib/api'

/** Re-fetch at most this often — the value changes rarely. */
const SYNC_TTL_MS = 5 * 60 * 1000

let lastSyncAt = Number.NEGATIVE_INFINITY
let inFlight: Promise<void> | null = null

/**
 * Refresh the undo-send cache from the server, at most once per TTL. Safe to
 * call on every composer mount: it coalesces concurrent calls, throttles by
 * time, and never throws (offline / not-configured simply keeps the cache).
 * `now`/`fetchImpl` are injectable for tests.
 */
export function syncUndoSendFromServer(
  now: number = Date.now(),
  fetchImpl: typeof emailApiFetch = emailApiFetch,
): Promise<void> {
  if (inFlight) return inFlight
  if (now - lastSyncAt < SYNC_TTL_MS) return Promise.resolve()
  lastSyncAt = now // throttle regardless of outcome so failures don't hammer
  inFlight = (async () => {
    try {
      const res = await fetchImpl('/api/settings/email/')
      if (!res.ok) return
      const data = await res.json().catch(() => null)
      const prefs = data?.preferences ?? data
      const secs = prefs?.undo_send_seconds
      if (secs != null) setUndoSendSeconds(Number(secs))
    } catch {
      /* offline / endpoint unavailable — keep the cached value */
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

/** Test-only: reset the module throttle state. */
export function __resetUndoSendSyncForTests(): void {
  lastSyncAt = Number.NEGATIVE_INFINITY
  inFlight = null
}
